/**
 * @module manifest/visibility/visibilityFactories
 * @description 配置项显隐条件的可复用工厂函数（供 groupRegistryDefs.chartVisibility 引用）
 *
 * 数据流：
 *   Panel 层从 QueryContext 获取 QB 状态 → 构造 QBStateSnapshot
 *   → 调用 Config A 中的 visible() 函数 → 决定配置项显隐
 *
 * 关键：
 * - 这些工厂函数是纯函数，零副作用，可在 Config 声明和 useMemo 中安全使用
 * - QBStateSnapshot 字段来源：QueryContext（chartType/mode/columns...）
 * - 仅包含影响配置项显隐的字段，不包含全部 QB 状态
 *
 * @see PRD 配置项模块 §00 — 各图表类型的配置项显隐条件
 * @see PRD 配置面板联动方案 §11 — QB 变化影响配置项显隐
 */

import { ChartType, type TChartType } from '@dbl/core/chart';
import {
  detectDateDimensionsInList,
  isFirstDimensionDate,
} from '../dimensionDateUtils';
import type { QBStateSnapshot } from '../../types';

/**
 * 显隐条件函数类型
 * @param qb - QB 状态快照
 * @returns true=配置项可见，false=隐藏
 */
export type VisibilityFn = (qb: QBStateSnapshot) => boolean;

// ── 原子工厂 ──

/** 至少有一个维度时可见 */
export const hasDimensions: VisibilityFn = (qb) => qb.columnDimensions.length >= 1;

/** 恰好一个指标时可见 */
export const hasOneMetric: VisibilityFn = (qb) => qb.columnMetrics.length === 1;

/** 多个指标时可见 */
export const hasMultiMetrics: VisibilityFn = (qb) => qb.columnMetrics.length >= 2;

/** 多个分类维度时可见 */
export const hasMultiDimensions: VisibilityFn = (qb) =>
  qb.columnDimensions.length > 1;

/** QB 快照中是否含 date 维度（优先读列数据，与 hasDateDimension 字段对齐） */
export const qbHasDateDimension: VisibilityFn = (qb) =>
  detectDateDimensionsInList(qb.columnDimensions) || qb.hasDateDimension;

/** 没有日期维度时可见 */
export const hasNoDateDim: VisibilityFn = (qb) => !qbHasDateDimension(qb);

/** 有日期维度时可见 */
export const hasDateDimension: VisibilityFn = (qb) => qbHasDateDimension(qb);

/** 任一维度为 date（ChartsDrop: dimensionContainsDate） */
export const dimensionContainsDate: VisibilityFn = (qb) =>
  detectDateDimensionsInList(qb.columnDimensions);

/** 首个维度为 date（ChartsDrop: dimensionOneContainsDate） */
export const firstDimensionIsDate: VisibilityFn = (qb) =>
  isFirstDimensionDate(qb.columnDimensions);

/**
 * 组合图 X 轴排序方式：仅首维非 date 时展示
 * 对齐 ChartsDrop MixedChart：dimensionOneContainsDate → 隐藏排序相关子项
 */
export const supportsMixedChartXAxisSortBy: VisibilityFn = (qb) =>
  hasDimensions(qb) && !firstDimensionIsDate(qb);

/** 聚合模式下可见 */
export const isAggregation: VisibilityFn = (qb) => qb.mode === 'aggregation';

/** 非交叉透视表时可见（合并相同单元格仅普通扁平行表支持） */
export const notCrosstab: VisibilityFn = (qb) => !qb.isCrosstabActive;

/** 选中手动排序时可见 */
export const isManualSort: VisibilityFn = (qb) =>
  qb.sortType === 'customMetricSort'
    || qb.sortType === 'metric'
    || qb.sortType === 'manual'
    || qb.sortType === 'x-axis';

/** 未开启双Y轴时可见 */
export const notDualYAxis: VisibilityFn = (qb) => !qb.dualYAxisEnabled;

/** 开启了双Y轴时可见 */
export const isDualYAxis: VisibilityFn = (qb) => qb.dualYAxisEnabled;

// ── 组合工厂 ──

/** 所有条件同时满足 */
export const and = (...rules: VisibilityFn[]): VisibilityFn => (qb) =>
  rules.every((r) => r(qb));

/** 任一条件满足 */
export const or = (...rules: VisibilityFn[]): VisibilityFn => (qb) =>
  rules.some((r) => r(qb));

/** 条件取反 */
export const not = (rule: VisibilityFn): VisibilityFn => (qb) => !rule(qb);

/** 始终可见 */
export const always: VisibilityFn = () => true;

// ── X 轴排序子项（V2 简化三态 Select）──

const BAR_FAMILY_WITH_MANUAL = [
  ChartType.Bar,
  ChartType.StackBar,
  ChartType.PercentStackBar,
];

/** 折线图族（折线 / 事件折线） */
const isLineFamilyChart = (chartType: TChartType): boolean =>
  chartType === ChartType.Line || chartType === ChartType.EventLine;

/** 折线 X 轴规则适用（含百分比折线，不含事件折线） */
const isLineXAxisFamilyChart = (chartType: TChartType): boolean =>
  chartType === ChartType.Line || chartType === ChartType.PercentLine;

/** X 轴：是否展示「手动排序」选项 */
export const supportsXAxisManualSort: VisibilityFn = (qb) => {
  if (qb.chartType === ChartType.HorizontalBarChart) return false;
  if (isLineFamilyChart(qb.chartType)) return true;
  if (BAR_FAMILY_WITH_MANUAL.includes(qb.chartType)) return true;
  if (qb.chartType === ChartType.MixedChart) {
    return supportsMixedChartXAxisSortBy(qb);
  }
  // 饼图：仅默认排序 + 指标排序，无手动排序
  if (qb.chartType === ChartType.Pie) return false;
  return false;
};

/** X 轴：是否展示「指标排序」选项 */
export const supportsXAxisMetricSort: VisibilityFn = (qb) => {
  if (qb.chartType === ChartType.Pie) return qb.columnMetrics.length >= 1;
  if (BAR_FAMILY_WITH_MANUAL.includes(qb.chartType)) return true;
  if (qb.chartType === ChartType.MixedChart) {
    return supportsMixedChartXAxisSortBy(qb);
  }
  return false;
};

/** 支持 X 轴标签方向的图表类型 */
const X_AXIS_LABEL_CHART_TYPES = [
  ChartType.Bar,
  ChartType.StackBar,
  ChartType.PercentStackBar,
  ChartType.Line,
  ChartType.PercentLine,
  ChartType.MixedChart,
  ChartType.EventLine,
];

/** X 轴标签方向 — 柱状/堆叠/折线/组合/事件折线 */
export const supportsXAxisLabelDirection: VisibilityFn = (qb) =>
  X_AXIS_LABEL_CHART_TYPES.includes(qb.chartType);

/** X 轴 TopN — 饼图/柱状图/堆叠/百分比堆叠各有独立逻辑 */
const X_AXIS_TOPN_MULTI_DIM_CHART_TYPES: TChartType[] = [];

export const supportsXAxisTopN: VisibilityFn = (qb) => {
  if (qb.chartType === ChartType.Pie) return true;
  // 柱状/堆叠/百分比堆叠：仅单指标时可见（对齐 PRD 拖拽矩阵）
  if (
    qb.chartType === ChartType.Bar
    || qb.chartType === ChartType.StackBar
    || qb.chartType === ChartType.PercentStackBar
  ) {
    return hasOneMetric(qb);
  }
  return (
    X_AXIS_TOPN_MULTI_DIM_CHART_TYPES.includes(qb.chartType) &&
    hasMultiDimensions(qb)
  );
};

/** X 轴名称 — 仅横向条形图 */
export const supportsXAxisName: VisibilityFn = (qb) =>
  qb.chartType === ChartType.HorizontalBarChart;

/** 折线图展示限制 — 折线/百分比折线且分类维度 > 1（事件折线不展示） */
export const showLineDisplayLimit: VisibilityFn = (qb) =>
  isLineXAxisFamilyChart(qb.chartType) && qb.columnDimensions.length > 1;

/**
 * X 轴卡片内「排序方式」— 饼图 / 柱状图族（组合图排序在独立配置项，不在 X 轴卡内）
 *
 * `SortBy`：有分类维度才为 true，兼作 formDelKey 上传门控（无维不传 sort 字段）。
 * 柱状图族排序 UI 不依赖 SortBy：MetricSort/ManualSort 恒 true；
 * DatasetFieldSort 也须常显，否则删维后会把已选「字段排序」回退成默认并清空 chartSorts
 *（相对 release 的回归：release 无字段排序态，Metric/Manual 删维不回退）。
 */
export const supportsXAxisSortInCard: VisibilityFn = (qb) => {
  if (qb.chartType === ChartType.Pie) return true;
  if (BAR_FAMILY_WITH_MANUAL.includes(qb.chartType)) {
    return qb.columnDimensions.length >= 1;
  }
  return false;
};

/**
 * 柱状图族「字段排序」选项显隐 — 不跟 SortBy 绑维度。
 * 与 MetricSort/ManualSort 一样撑住排序 UI，避免删维触发 draft 回退。
 * 饼图仅默认 + 指标排序，不展示字段排序。
 */
export const supportsBarFamilyDatasetFieldSort: VisibilityFn = (qb) =>
  BAR_FAMILY_WITH_MANUAL.includes(qb.chartType);

/** XAxisSetting 整卡显隐 */
export const isXAxisSettingVisible: VisibilityFn = (qb) => {
  // 组合图：X 轴卡仅展示轴标签（x_label）
  if (qb.chartType === ChartType.MixedChart) return true;
  if (supportsXAxisName(qb)) return true;
  // 柱/堆叠/百分比堆叠：轴标签方向仅看图表类型 → 删维整卡仍在
  if (supportsXAxisLabelDirection(qb)) return true;
  if (qb.chartType === ChartType.Pie) return true;
  if (BAR_FAMILY_WITH_MANUAL.includes(qb.chartType)) {
    return qb.columnDimensions.length >= 1;
  }
  return false;
};

// ── 折线图 X 轴子项（Line）──

/**
 * 折线图 X 轴 TopN：
 *   单指标 +（多维度，或 单维度且首维非 date）
 */
const supportsLineXAxisTopN: VisibilityFn = (qb) => {
  if (!isLineXAxisFamilyChart(qb.chartType)) return false;
  if (!hasOneMetric(qb)) return false;
  if (hasMultiDimensions(qb)) return true;
  return hasDimensions(qb) && !firstDimensionIsDate(qb);
};

/**
 * 折线图 X 轴排序整块（SortBy / ManualSort）：
 *   有维度且第一个维度不是 date 时展示
 */
export const supportsLineFamilyXAxisSortBy: VisibilityFn = (qb) =>
  isLineXAxisFamilyChart(qb.chartType)
  && hasDimensions(qb)
  && !firstDimensionIsDate(qb);

/** @deprecated 使用 supportsLineFamilyXAxisSortBy */
export const supportsLineXAxisSortBy = supportsLineFamilyXAxisSortBy;

/**
 * 折线图 X 轴「指标排序」：
 *   排序整块可见 + 单维度 + 单指标
 */
export const supportsLineFamilyXAxisMetricSort: VisibilityFn = (qb) =>
  isLineXAxisFamilyChart(qb.chartType)
  && supportsLineFamilyXAxisSortBy(qb)
  && !hasMultiDimensions(qb)
  && hasOneMetric(qb);

/** @deprecated 使用 supportsLineFamilyXAxisMetricSort */
export const supportsLineXAxisMetricSort = supportsLineFamilyXAxisMetricSort;

/** 折线图 XAxisSetting 子项显隐表 */
export const lineFamilyXAxisItemsVisibility: Record<string, VisibilityFn> = {
  XAxisName: supportsXAxisName,
  SortBy: supportsLineFamilyXAxisSortBy,
  MetricSort: supportsLineFamilyXAxisMetricSort,
  ManualSort: supportsLineFamilyXAxisSortBy,
  /** 字段排序：与排序整块同条件（有维且首维非 date） */
  DatasetFieldSort: supportsLineFamilyXAxisSortBy,
  TopN: supportsLineXAxisTopN,
  XAxisLabelDirection: supportsXAxisLabelDirection,
};

/** @deprecated 使用 lineFamilyXAxisItemsVisibility */
export const lineXAxisItemsVisibility = lineFamilyXAxisItemsVisibility;

// ── 事件折线图 X 轴子项（EventLine，与折线图规则不同）──

/**
 * 事件折线 X 轴 TopN：
 *   单指标 +（多维度，或 单维度且首维非 date）
 */
const supportsEventLineXAxisTopN: VisibilityFn = (qb) => {
  if (qb.chartType !== ChartType.EventLine) return false;
  if (!hasOneMetric(qb)) return false;
  if (hasMultiDimensions(qb)) return true;
  return hasDimensions(qb) && !firstDimensionIsDate(qb);
};

/**
 * 事件折线 X 轴排序整块（SortBy）：
 *   仅单维度 + 无 date 维度 + 单指标
 */
export const supportsEventLineXAxisSortBy: VisibilityFn = (qb) =>
  qb.chartType === ChartType.EventLine
  && hasDimensions(qb)
  && !hasMultiDimensions(qb)
  && !dimensionContainsDate(qb)
  && hasOneMetric(qb);

/** 事件折线「指标排序」— 与排序整块同条件（默认 + 指标，无手动） */
export const supportsEventLineXAxisMetricSort: VisibilityFn = (qb) =>
  supportsEventLineXAxisSortBy(qb);

/** 事件折线不展示「手动排序」 */
export const supportsEventLineXAxisManualSort: VisibilityFn = () => false;

/** 事件折线图 XAxisSetting 子项显隐表 */
export const eventLineXAxisItemsVisibility: Record<string, VisibilityFn> = {
  XAxisName: supportsXAxisName,
  SortBy: supportsEventLineXAxisSortBy,
  MetricSort: supportsEventLineXAxisMetricSort,
  ManualSort: supportsEventLineXAxisManualSort,
  /** 字段排序：事件折线图不展示 */
  DatasetFieldSort: () => false,
  TopN: supportsEventLineXAxisTopN,
  XAxisLabelDirection: supportsXAxisLabelDirection,
};

// ── 默认 XAxisSetting itemsVisibility 表 ──

/** XAxisSetting 分类组件 itemsVisibility 默认表 */
export const xAxisSettingItemsVisibility: Record<string, VisibilityFn> = {
  XAxisName: supportsXAxisName,
  /** 上传门控：无分类维度时 false → formDelKey 过滤 sort 字段 */
  SortBy: supportsXAxisSortInCard,
  MetricSort: supportsXAxisMetricSort,
  ManualSort: supportsXAxisManualSort,
  /**
   * 字段排序：柱状族与 Metric/Manual 一样不绑维度（饼图不展示）。
   * 若跟 SortBy 同条件，删维会触发 useXAxisSortDraft 回退清空字段排序。
   */
  DatasetFieldSort: supportsBarFamilyDatasetFieldSort,
  TopN: supportsXAxisTopN,
  XAxisLabelDirection: supportsXAxisLabelDirection,
};

/**
 * 组合图 X 轴子项显隐（对齐 ChartsDrop MixedChart：无 TopN/轴名称；排序见 supportsMixedChartXAxisSortBy）
 */
export const mixedChartXAxisItemsVisibility: Record<string, VisibilityFn> = {
  XAxisName: () => false,
  SortBy: supportsMixedChartXAxisSortBy,
  MetricSort: supportsMixedChartXAxisSortBy,
  ManualSort: supportsMixedChartXAxisSortBy,
  /** 字段排序：组合图与排序整块同条件（有维且首维非 date） */
  DatasetFieldSort: supportsMixedChartXAxisSortBy,
  TopN: () => false,
  XAxisLabelDirection: always,
};

/**
 * 条形图 X 轴子项显隐（对齐 ChartsDrop：isShowXAxisSort=false，排序在 Y 轴）
 *   X 轴仅展示轴名称，无排序方式 / TopN / 标签方向
 */
export const horizontalBarXAxisItemsVisibility: Record<string, VisibilityFn> = {
  XAxisName: supportsXAxisName,
  SortBy: () => false,
  MetricSort: () => false,
  ManualSort: () => false,
  DatasetFieldSort: () => false,
  TopN: () => false,
  XAxisLabelDirection: () => false,
};

/** @deprecated 使用 xAxisSettingItemsVisibility */
export const xAxisSortItemsVisibility = xAxisSettingItemsVisibility;

// ── Y 轴配置卡片 ──

/** 支持 Y 轴配置卡片的图表类型 */
const Y_AXIS_SETTING_CHART_TYPES = [
  ChartType.Bar,
  ChartType.HorizontalBarChart,
  ChartType.StackBar,
  ChartType.PercentStackBar,
  ChartType.PercentLine,
  ChartType.Line,
  ChartType.EventLine,
];

/** YAxisSetting 整卡显隐 */
export const isYAxisSettingVisible: VisibilityFn = (qb) =>
  Y_AXIS_SETTING_CHART_TYPES.includes(qb.chartType);

/** 折线/事件折线 · 双 Y 轴开关（卡片内子项） */
export const supportsDualYAxisInCard: VisibilityFn = (qb) =>
  isLineFamilyChart(qb.chartType);

/** 单轴名称/范围 — 折线双轴开启时由主/次轴接管 */
export const showSingleYAxisFieldsInCard: VisibilityFn = (qb) => {
  if (isLineFamilyChart(qb.chartType)) {
    return !qb.dualYAxisEnabled;
  }
  return true;
};

/** Y 轴范围开关 — 柱状图/堆叠图/折线图/事件折线图 */
const Y_AXIS_RANGE_CHART_TYPES = [
  ChartType.Bar,
  ChartType.StackBar,
  ChartType.Line,
  ChartType.EventLine,
];

export const supportsYAxisRangeSwitch: VisibilityFn = (qb) =>
  Y_AXIS_RANGE_CHART_TYPES.includes(qb.chartType);

// ── 条形图 Y 轴 TopN / 排序 ──

/** 条形图 Y 轴 TopN — 仅单指标（isShowYaxisTopN） */
export const supportsHorizontalBarYAxisTopN: VisibilityFn = (qb) =>
  qb.chartType === ChartType.HorizontalBarChart && hasOneMetric(qb);

/**
 * 条形图 Y 轴 SortBy — 仅作 formDelKey 上传门控（有分类维度才上传 sort）。
 * UI 由 ManualSort / MetricSort / DatasetFieldSort 撑住，避免删维整块消失并回退 draft。
 */
export const supportsHorizontalBarYAxisSortBy: VisibilityFn = (qb) =>
  qb.chartType === ChartType.HorizontalBarChart && hasDimensions(qb);

/**
 * 条形图 Y 轴 — 指标排序选项：
 * 无维度时仍展示（撑 UI、避免回退）；有维度时首维为 date 则隐藏。
 */
export const supportsHorizontalBarYAxisMetricSort: VisibilityFn = (qb) =>
  qb.chartType === ChartType.HorizontalBarChart
  && (!hasDimensions(qb) || !firstDimensionIsDate(qb));

/** 条形图 Y 轴 — 手动排序选项（UI 常显，不绑维度） */
export const supportsHorizontalBarYAxisManualSort: VisibilityFn = (qb) =>
  qb.chartType === ChartType.HorizontalBarChart;

/** 条形图 Y 轴 — 字段排序选项（UI 常显，避免删维回退清空） */
export const supportsHorizontalBarYAxisDatasetFieldSort: VisibilityFn = (qb) =>
  qb.chartType === ChartType.HorizontalBarChart;

/**
 * 条形图 YAxisSetting 子项显隐
 * SortBy=有维（上传门控）；其余排序子项无维仍可见
 */
export const horizontalBarYAxisItemsVisibility: Record<string, VisibilityFn> = {
  DualYAxis: () => false,
  YAxisName: showSingleYAxisFieldsInCard,
  YAxisRange: () => false,
  TopN: supportsHorizontalBarYAxisTopN,
  SortBy: supportsHorizontalBarYAxisSortBy,
  MetricSort: supportsHorizontalBarYAxisMetricSort,
  ManualSort: supportsHorizontalBarYAxisManualSort,
  DatasetFieldSort: supportsHorizontalBarYAxisDatasetFieldSort,
};

/** 条形图图例系列排序 — 多维度时展示（dimensions > 1，与柱状图一致） */
export const supportsHorizontalBarLegendSeriesSort: VisibilityFn = (qb) =>
  qb.chartType === ChartType.HorizontalBarChart && hasMultiDimensions(qb);

/** 条形图 LegendSetting 子项显隐 */
export const horizontalBarLegendItemsVisibility: Record<string, VisibilityFn> = {
  SeriesSortBy: supportsHorizontalBarLegendSeriesSort,
};

/** YAxisSetting 分类组件 itemsVisibility 默认表 */
export const yAxisSettingItemsVisibility: Record<string, VisibilityFn> = {
  DualYAxis: supportsDualYAxisInCard,
  YAxisName: showSingleYAxisFieldsInCard,
  YAxisRange: and(showSingleYAxisFieldsInCard, supportsYAxisRangeSwitch),
};

// ── 浮窗配置 ToolTipSetting（对齐 ChartsDrop isShowTooltipSetting + 子项 CSS 隐藏规则）──

const TOOLTIP_SETTING_CHART_TYPES = [
  ChartType.MixedChart,
  ChartType.StackBar,
  ChartType.PercentStackBar,
];

/** ToolTipSetting 整卡显隐 — 组合图 / 堆叠图 / 百分比堆叠图 */
export const isToolTipSettingVisible: VisibilityFn = (qb) =>
  TOOLTIP_SETTING_CHART_TYPES.includes(qb.chartType);

/** 显示总计 — 组合图隐藏 */
export const tooltipShowTotalVisible: VisibilityFn = (qb) =>
  qb.chartType !== ChartType.MixedChart;

/** 显示占比 — 组合图、百分比堆叠隐藏 */
export const tooltipShowShareVisible: VisibilityFn = (qb) =>
  qb.chartType !== ChartType.MixedChart &&
  qb.chartType !== ChartType.PercentStackBar;

/** 额外内容 — 堆叠图、百分比堆叠隐藏 */
export const tooltipExtraVisible: VisibilityFn = (qb) =>
  qb.chartType !== ChartType.StackBar &&
  qb.chartType !== ChartType.PercentStackBar;

/** ToolTipSetting 子项 itemsVisibility */
export const toolTipSettingItemsVisibility: Record<string, VisibilityFn> = {
  ShowTotal: tooltipShowTotalVisible,
  ShowShare: tooltipShowShareVisible,
  TooltipExtra: tooltipExtraVisible,
};

// ── 图表切换 ChartSwitch（对齐 ChartsDrop：表格 / 柱状图 / 折线图）──

const CHART_SWITCH_CHART_TYPES = [
  ChartType.Table,
  ChartType.Bar,
  ChartType.Line,
];

/** ChartSwitch 整卡显隐 */
export const isChartSwitchVisible: VisibilityFn = (qb) =>
  CHART_SWITCH_CHART_TYPES.includes(qb.chartType);

